/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Send, CheckCircle, AlertCircle, ChevronLeft, ChevronRight, Check } from "lucide-react";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import { getExerciseIcon } from "../sharedUtils";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MergedLesson {
    id: number;
    lessonNumber: number;
    title: string;
    content: string;
    image: string | null;
    exercises: any[];
}

export default function UserTrainingExecutionPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToast } = useToaster();

    const [lessons, setLessons] = useState<MergedLesson[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentLessonIndex, setCurrentLessonIndex] = useState(0);

    // Submissions State
    const [userAnswers, setUserAnswers] = useState<Record<number, string>>({});
    const [submittingId, setSubmittingId] = useState<number | null>(null);
    const [finishing, setFinishing] = useState(false);
    const submitInFlightRef = useRef(false);
    const activeTrainingIdRef = useRef(id);
    activeTrainingIdRef.current = id;

    useEffect(() => {
        if (!id) return;
        const fetchTrainingData = async () => {
            setLoading(true);
            try {
                // Fetch lessons and exercises concurrently
                const [trainingsRes, exercisesRes] = await Promise.all([
                    apiService.get(`api/gp/trainings/${id}`),
                    apiService.get(`api/gp/exercises/${id}`)
                ]);

                const lessonsData = trainingsRes.data?.lessons || [];
                const exercisesData = exercisesRes.data || [];

                // Merge exercises into lessons based on lessonId
                const merged: MergedLesson[] = lessonsData.map((l: any) => ({
                    ...l,
                    exercises: exercisesData.find((e: any) => e.lessonId === l.id)?.exercises || []
                }));

                setLessons(merged);

                // Pre-fill existing answers from the backend if the user already attempted them
                const existingAnswers: Record<number, string> = {};
                merged.forEach(lesson => {
                    lesson.exercises.forEach(ex => {
                        if (ex.attempted && ex.userAnswer) {
                            existingAnswers[ex.id] = ex.userAnswer;
                        }
                    });
                });
                setUserAnswers(existingAnswers);

            } catch (error: any) {
                addToast(error?.response?.data?.error || "Failed to load training.", "error");
            } finally {
                setLoading(false);
            }
        };
        fetchTrainingData();
    }, [id, addToast]);

    const isExerciseAttempted = (exerciseId: number) =>
        lessons.some(lesson => lesson.exercises.some(ex => ex.id === exerciseId && ex.attempted));

    const handleSubmitAnswer = async (exerciseId: number) => {
        const answer = userAnswers[exerciseId];
        if (!answer?.trim() || submitInFlightRef.current || isExerciseAttempted(exerciseId)) return;
        submitInFlightRef.current = true;
        const trainingIdAtSubmit = id;

        try {
            setSubmittingId(exerciseId);
            const res = await apiService.post("api/gp/exercises/submit", { exerciseId, userAnswer: answer });
            if (activeTrainingIdRef.current !== trainingIdAtSubmit) return;

            // Update the local state with the result so the user sees immediate feedback
            setLessons(prev => prev.map(lesson => ({
                ...lesson,
                exercises: lesson.exercises.map(ex => ex.id === exerciseId ? {
                    ...ex,
                    attempted: true,
                    correct: res.data.correct,
                    score: res.data.score,
                    feedback: res.data.feedback,
                    correctAnswer: res.data.correctAnswer
                } : ex)
            })));
            setUserAnswers(prev => ({ ...prev, [exerciseId]: answer }));
            addToast("Answer submitted successfully.", "success");
        } catch (error: any) {
            if (activeTrainingIdRef.current !== trainingIdAtSubmit) return;
            addToast(error?.response?.data?.error || "Failed to submit answer.", "error");
        } finally {
            submitInFlightRef.current = false;
            setSubmittingId(null);
        }
    };

    const handleFinishTraining = async () => {
        try {
            setFinishing(true);
            await apiService.post(`api/gp/exercises/${id}/finish`, {});
            addToast("Training completed successfully!", "success");
            navigate("/admin/training");
        } catch (error: any) {
            addToast(error?.response?.data?.error || "Failed to finish training.", "error");
        } finally {
            setFinishing(false);
        }
    };

    // Check if ALL exercises across ALL lessons have been attempted
    const allExercisesAttempted = lessons.every(l => l.exercises.every(e => e.attempted === true));

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-[calc(100vh-100px)]">
                <RefreshCw className="animate-spin text-blue-500 mb-4" size={32} />
                <p className="text-gray-500 font-medium">Loading training module...</p>
            </div>
        );
    }

    const currentLesson = lessons[currentLessonIndex];
    if (!currentLesson) return null;

    return (
        <div className="flex flex-col h-full min-h-[calc(100vh-100px)] bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden transition-colors duration-300 border border-gray-200 dark:border-gray-800">

            {/* HEADER */}
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 shrink-0 flex items-center gap-4 sticky top-0 z-20 shadow-sm">
                <button onClick={() => navigate("/admin/training")} className="p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 rounded-lg transition-colors">
                    <ArrowLeft size={20} />
                </button>
                <div className="flex-1 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate pr-4">Module {currentLessonIndex + 1}: {currentLesson.title}</h2>
                    <span className="hidden sm:inline-block text-sm font-medium text-gray-500 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-md">
                        Lesson {currentLessonIndex + 1} of {lessons.length}
                    </span>
                </div>
            </div>

            {/* CONTENT */}
            <div className="flex-1 overflow-y-auto p-6 lg:p-8">
                <div className="max-w-4xl mx-auto space-y-8">

                    {/* LESSON READING MATERIAL */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
                        <div className="p-8 prose dark:text-white dark:prose-invert max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {currentLesson.content}
                            </ReactMarkdown>
                            {currentLesson.image && (
                                <div className="mt-6 flex justify-center">
                                    <img src={currentLesson.image} alt="Lesson illustration" className="max-h-96 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* LESSON EXERCISES */}
                    {currentLesson.exercises.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
                            <div className="px-8 py-5 bg-blue-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Knowledge Check</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Answer the following questions to complete this lesson.</p>
                            </div>

                            <div className="p-8 divide-y divide-gray-100 dark:divide-gray-700/50">
                                {currentLesson.exercises.map((ex: any, idx: number) => {
                                    const isOptions = ex.options && ex.options.length > 0;
                                    const currentValue = userAnswers[ex.id] || "";
                                    const isAttempted = ex.attempted;
                                    const isSubmitting = submittingId === ex.id;
                                    const isInputLocked = isAttempted || isSubmitting;

                                    return (
                                        <div key={ex.id} className={`${idx !== 0 ? 'pt-10 mt-10' : ''}`}>
                                            <div className="flex items-start gap-4">
                                                <div className={`mt-1 p-2.5 rounded-xl border shadow-sm ${isAttempted ? (ex.correct ? 'bg-green-50 border-green-200 text-green-600 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800') : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-blue-600 dark:text-blue-400'}`}>
                                                    {getExerciseIcon(ex.type)}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-5">{idx + 1}. {ex.question}</p>

                                                    <div className="mb-5">
                                                        {isOptions ? (
                                                            <div className="grid gap-3">
                                                                {ex.options.map((opt: string, i: number) => (
                                                                    <label key={i} className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${isAttempted ? (currentValue === opt ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/30 dark:border-blue-700 cursor-not-allowed opacity-80' : 'border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-50') : (currentValue === opt ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/30 dark:border-blue-700 shadow-sm cursor-pointer' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer')}`}>
                                                                        <input type="radio" value={opt} checked={currentValue === opt} disabled={isInputLocked} onChange={(e) => setUserAnswers(prev => ({ ...prev, [ex.id]: e.target.value }))} className="w-5 h-5 text-blue-600 focus:ring-blue-500 disabled:opacity-50" />
                                                                        <span className="text-base font-medium text-gray-700 dark:text-gray-300">{opt}</span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <textarea rows={4} value={currentValue} disabled={isInputLocked} onChange={(e) => setUserAnswers(prev => ({ ...prev, [ex.id]: e.target.value }))} placeholder="Type your answer here..." className="w-full p-4 text-base rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-75 disabled:cursor-not-allowed transition-colors" />
                                                        )}
                                                    </div>

                                                    {!isAttempted ? (
                                                        <button onClick={() => handleSubmitAnswer(ex.id)} disabled={!currentValue || submittingId !== null} className="flex items-center gap-2 px-6 py-3 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm">
                                                            {isSubmitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send size={16} />} Submit Answer
                                                        </button>
                                                    ) : (
                                                        <div className={`p-5 rounded-xl border animate-in fade-in slide-in-from-top-2 ${ex.correct ? 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-900/30' : 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-900/30'}`}>
                                                            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-black/5 dark:border-white/10">
                                                                {ex.correct ? <CheckCircle className="text-green-600 dark:text-green-400" size={20} /> : <AlertCircle className="text-red-600 dark:text-red-400" size={20} />}
                                                                <span className={`text-base font-bold ${ex.correct ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                                                                    {ex.correct ? 'Correct' : 'Incorrect'} <span className="opacity-75 font-medium ml-1">(Score: {ex.score})</span>
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-800 dark:text-gray-200 mb-3 leading-relaxed"><strong>Feedback:</strong> {ex.feedback}</p>
                                                            {ex.correctAnswer && (
                                                                <div className="p-3 bg-white/60 dark:bg-gray-900/40 rounded border border-gray-200 dark:border-gray-700/50">
                                                                    <p className="text-sm text-gray-800 dark:text-gray-200"><strong>Correct Answer:</strong> {ex.correctAnswer}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* NEXT / PREVIOUS / FINISH NAVIGATION */}
                    <div className="flex justify-between items-center pt-4">
                        <button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setCurrentLessonIndex(prev => prev - 1); }} disabled={currentLessonIndex === 0} className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-0 disabled:pointer-events-none transition-colors font-medium">
                            <ChevronLeft size={16} /> Previous Lesson
                        </button>

                        {currentLessonIndex < lessons.length - 1 ? (
                            <button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setCurrentLessonIndex(prev => prev + 1); }} className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium">
                                Next Lesson <ChevronRight size={16} />
                            </button>
                        ) : (
                            <button
                                onClick={handleFinishTraining}
                                disabled={!allExercisesAttempted || finishing}
                                title={!allExercisesAttempted ? "You must answer all questions across all lessons to finish." : ""}
                                className="flex items-center gap-2 px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-sm"
                            >
                                {finishing ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check size={18} />} Finish Training
                            </button>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
}